"use client";

import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CardDescription, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { KeywordPlatformsProps } from "../../types/keyword.types";

export function KeywordPlatforms({
  selectedPlatforms,
  setSelectedPlatforms,
  enabledPlatforms = [],
  isApiTokenLoading = false,
}: KeywordPlatformsProps) {
  const platforms = useMemo(
    () => [
      { id: "facebook", label: "Facebook" },
      { id: "twitter", label: "X/Twitter" },
      { id: "instagram", label: "Instagram" },
      { id: "youtube", label: "YouTube" },
      { id: "reddit", label: "Reddit" },
      { id: "tiktok", label: "TikTok" },
    ],
    [],
  );

  const handlePlatformChange = (platformId: string, checked: boolean) => {
    setSelectedPlatforms((prev) =>
      checked ? [...prev, platformId] : prev.filter((id) => id !== platformId),
    );
  };

  const handleSelectAll = () => {
    // Only select platforms that are enabled
    const availablePlatforms =
      enabledPlatforms.length > 0
        ? platforms
            .filter((p) => enabledPlatforms.includes(p.id))
            .map((p) => p.id)
        : platforms.map((p) => p.id);
    setSelectedPlatforms(availablePlatforms);
  };

  const handleClearAll = () => {
    setSelectedPlatforms([]);
  };

  // Check if a platform is disabled
  const isPlatformDisabled = (platformId: string) => {
    return (
      enabledPlatforms.length > 0 && !enabledPlatforms.includes(platformId)
    );
  };

  const platformItems = useMemo(
    () =>
      platforms.map((platform) => {
        const isDisabled = isPlatformDisabled(platform.id);

        return (
          <TooltipProvider key={platform.id}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={platform.id}
                    checked={selectedPlatforms.includes(platform.id)}
                    onCheckedChange={(checked) =>
                      handlePlatformChange(platform.id, checked as boolean)
                    }
                    disabled={isDisabled || isApiTokenLoading}
                    className="flex-shrink-0"
                  />
                  <Label
                    htmlFor={platform.id}
                    className={`truncate text-sm font-semibold ${
                      isDisabled
                        ? "cursor-not-allowed text-neutral-400"
                        : "cursor-pointer text-neutral-800"
                    }`}
                  >
                    {platform.label}
                  </Label>
                </div>
              </TooltipTrigger>
              {isDisabled && (
                <TooltipContent>
                  <p>This platform is not available in your subscription</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [platforms, selectedPlatforms, enabledPlatforms],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex-shrink-0">
        <div className="flex w-full flex-row items-center gap-4">
          <div className="min-w-0 flex-1">
            <CardTitle className="leading-normal">Platforms</CardTitle>
            <CardDescription>
              Select the media sources you would like to receive
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2 sm:flex-nowrap">
            <Button onClick={handleSelectAll} variant="minimal">
              Select All
            </Button>
            <Button variant="destructive" onClick={handleClearAll}>
              Clear Platforms
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col">
        <div
          className="grid gap-3"
          style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          }}
        >
          {platformItems}
        </div>
      </div>
    </div>
  );
}
